import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { resolveBusinessErrorMessage } from '../errors/business-error-messages';
import { TopupApi } from '../apis/topup-api';
import type {
  TopupMethod,
  TopupToken,
  TopupAuthTxnResult,
  InitTopupResponse,
  TopupFee,
} from '../models';

/**
 * TopupService
 *
 * Business-logic layer between TopupStore and TopupApi.
 *
 * Responsibilities:
 *  - Unwrap API envelopes (`{ success, methods }` → `methods[]`)
 *  - Return raw API data; S3 URL resolution is done reactively in the UI layer
 *  - Keep TopupStore free of direct API or environment dependencies
 *
 * Architecture: Component → Store → Service → Api → Backend
 */
@Injectable({
  providedIn: 'root',
})
export class TopupService {
  private readonly api = inject(TopupApi);

  // ── 1. Methods ─────────────────────────────────────────────────────────────

  /**
   * Fetch topup methods available for a given amount. Icon paths are returned as
   * raw relative S3 keys; the S3 base URL is applied reactively in the UI layer
   * so icons resolve once runtime config loads.
   */
  getMethods(amount: number): Observable<TopupMethod[]> {
    return this.api.getMethods(amount).pipe(map((res) => res.methods));
  }

  // ── 2. Tokens ──────────────────────────────────────────────────────────────

  /**
   * Fetch saved tokens (payment accounts) for the given processor.
   */
  getTokens(processorId: string): Observable<TopupToken[]> {
    return this.api.getTokens(processorId).pipe(
      map((res) => {
        if (!res.success) {
          // HTTP 200 with `success: false` — the code is the only reliable signal.
          // Goes through the registry rather than an inline `=== '451'` so every
          // mapped business code gets its proper message here too.
          throw new Error(
            resolveBusinessErrorMessage(
              res.error_code,
              'Impossible de charger vos comptes enregistrés. Vérifiez votre connexion et réessayez.',
            ),
          );
        }
        return res.tokens ?? [];
      }),
    );
  }

  // ── 3. Auth transaction ────────────────────────────────────────────────────

  /**
   * Authorize the transaction.
   * Returns the full authorization result including fee, categories and PIN flag.
   *
   * @param amount      - Amount to top up
   * @param processorId - ID of the selected processor
   * @param tokenId     - ID of the selected token (_id)
   * @param methodId    - ID of the selected TopupMethod (method.id)
   */
  authTxn(
    amount: number,
    processorId: string,
    tokenId: string,
    methodId: string,
  ): Observable<TopupAuthTxnResult> {
    return this.api
      .authTxn(amount, processorId, tokenId, methodId)
      .pipe(map((res) => res.authorize_trxn));
  }

  // ── 4. Init topup ──────────────────────────────────────────────────────────

  /**
   * Execute the topup and return the raw backend response.
   */
  initTopup(
    amount: number,
    processorId: string,
    tokenId: string,
    categoryId: string,
    fees: { fee: TopupFee; commission: TopupFee },
    message: string,
    pin?: string,
  ): Observable<InitTopupResponse> {
    return this.api.initTopup(amount, processorId, tokenId, categoryId, fees, message, pin);
  }
}
